import { format, parseISO } from 'date-fns';
import { enums as e } from 'edrolo-ui';
import { SchoolClassType } from 'edrolo/enums/SchoolClassType';
import { Profile, ProfileSchoolClass } from 'edrolo/types/Profile';
import urlResolver from 'edrolo/utils/URLResolver';
import numeral from 'numeral';
import { MenuItem } from '../components/NavBar';
import { SchoolClassFeature } from '../types/SchoolClass';
import {
  AssignmentLifecycleStage,
  Task,
  TaskLifecycleStage,
} from '../types/Task';

export const getCategorizedSchoolClasses = (
  schoolClasses: Record<number, ProfileSchoolClass>
) => {
  const categorizedSchoolClasses: Record<string, MenuItem[]> = {};

  for (const key in schoolClasses) {
    const schoolClass = schoolClasses[key];
    const courseTitle = schoolClass.course_title;

    if (!categorizedSchoolClasses[courseTitle]) {
      categorizedSchoolClasses[courseTitle] = [];
    }

    categorizedSchoolClasses[courseTitle].push({
      id: schoolClass.school_class_id,
      title: schoolClass.school_class_title,
      to: urlResolver.reverse('course-page', {
        classId: schoolClass.school_class_id,
      }),
      isTrial:
        schoolClass.school_class_type === SchoolClassType.STUDENT_TRIAL ||
        schoolClass.school_class_type === SchoolClassType.TEACHER_TRIAL,
      expirationDate: schoolClass.school_class_expiration_date,
    });
  }

  return categorizedSchoolClasses;
};

export const formatDuration = (duration: number, isNoun?: boolean): string => {
  const formattedDuration = numeral(duration).format('00:00:00');

  const [hours, minutes] = formattedDuration.split(':');

  if (+hours !== 0) {
    return `${+hours} Hr ${+minutes} Min`;
  }
  if (+minutes === 0 && duration > 0) {
    return '< 1 Min';
  }
  return isNoun && +minutes > 1 ? `${+minutes} Mins` : `${+minutes} Min`;
};

// TODO remove after June 2024
export const NSW_COURSES_WITH_NEWLY_ADDED_QUESTION_SETS = {
  BIOLOGY: 48757,
  CHEMISTRY: 48849,
  PHYSICS: 48927,
  MATHEMATICS: 49160,
};

export const SecondsToHM = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  return { hours, minutes };
};

export const getFormattedTaskDuration = (task: Task): string => {
  const totalDurationInSeconds = task.total_duration;

  const { hours, minutes } = SecondsToHM(totalDurationInSeconds);
  const formattedDuration =
    hours > 0 ? `${hours} HR ${minutes} MINS` : `${minutes} MINS`;

  return formattedDuration;
};

export const formatDate = (date: DateISO8601 | string | Date) => {
  if (!date) {
    return '-';
  }
  const isoDate = parseISO(`${date}`);
  return format(isoDate, 'MMMM d');
};

export const truncateText = (text: string, maxLength: number): string => {
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

export const hasAccessToFeature = (
  profile: Profile,
  feature: SchoolClassFeature
) => {
  return Object.values(profile.school_classes).some((sc) =>
    sc.school_class_features.includes(feature)
  );
};

export const taskLifeCyclePillColor: Record<TaskLifecycleStage, e.ColorHue> = {
  [TaskLifecycleStage.DRAFT]: e.ColorHue.GREY,
  [TaskLifecycleStage.SCHEDULED]: e.ColorHue.INDIGO,
  [TaskLifecycleStage.OPEN]: e.ColorHue.TEAL,
  [TaskLifecycleStage.CLOSED]: e.ColorHue.GREEN,
  [TaskLifecycleStage.ARCHIVED]: e.ColorHue.AMBER,
};

export const taskLifeCyclePillText: Record<TaskLifecycleStage, string> = {
  [TaskLifecycleStage.DRAFT]: 'Draft',
  [TaskLifecycleStage.SCHEDULED]: 'Scheduled',
  [TaskLifecycleStage.OPEN]: 'Open',
  [TaskLifecycleStage.CLOSED]: 'Closed',
  [TaskLifecycleStage.ARCHIVED]: 'Archived',
};

export const assignmentLifeCyclePillColor: Record<
  AssignmentLifecycleStage,
  e.ColorHue
> = {
  [AssignmentLifecycleStage.IN_PROGRESS]: e.ColorHue.TEAL,
  [AssignmentLifecycleStage.NOT_STARTED]: e.ColorHue.GREY,
  [AssignmentLifecycleStage.COMPLETED]: e.ColorHue.GREEN,
  [TaskLifecycleStage.CLOSED]: e.ColorHue.GREEN,
  [AssignmentLifecycleStage.ARCHIVED]: e.ColorHue.AMBER,
};

export const assignmentLifeCyclePillText: Record<
  AssignmentLifecycleStage,
  string
> = {
  [AssignmentLifecycleStage.NOT_STARTED]: 'Not started',
  [AssignmentLifecycleStage.IN_PROGRESS]: 'In progress',
  [AssignmentLifecycleStage.COMPLETED]: 'Completed',
  [TaskLifecycleStage.CLOSED]: 'Closed',
  [AssignmentLifecycleStage.ARCHIVED]: 'Archived',
};
